import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sun,
  Moon,
  LogOut,
  User,
  Users,
  UserPlus,
  Building2,
  Calendar,
  FileText,
  Menu,
  X,
} from "lucide-react";

import { useAuth } from "../context/AuthProvider";
import { usePatient } from "../context/PatientContext";
import { useTheme } from "../context/ThemeContext";

const CNavbar = () => {
  const { auth, logout } = useAuth();
  const { doctorInfo, setDoctorInfo } = usePatient();
  const {
    theme,
    isDarkMode,
    toggleTheme,
    isTransliterationEnabled,
    toggleTransliteration
  } = useTheme();

  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const clinicList = doctorInfo?.CLINIC_LIST || [];

  /* ---------------- ROLE CONTROL ---------------- */
  const role = doctorInfo?.MANDALI_ROLE;

  const isAdmin = role === 1;
  const isDoctor = role === 2;
  const isAttendant = role === 3;

  const canAccessDoctorFeatures = isAdmin || isDoctor;
  /* ------------------------------------------------ */

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const handleClinicChange = (e) => {
    setDoctorInfo((prev) => ({
      ...prev,
      CLINIC_ID: Number(e.target.value),
    }));
  };

  const isActive = (path) => {
    if (path === "/patientlist") {
      return (
        location.pathname === "/patientlist" ||
        location.pathname.startsWith("/patient")
      );
    }
    return location.pathname === path;
  };

  if (!auth) return null;

  return (
    <>
      <div className="fixed top-0 left-0 right-0 z-50 flex justify-center p-3 md:p-6 pointer-events-none">
        <motion.nav
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className={`pointer-events-auto w-full max-w-[1500px] transition-all duration-500 rounded-[30px] border ${isDarkMode ? "border-white/10" : "border-gray-200"} shadow-lg backdrop-blur-md ${isDarkMode
            ? "bg-black/60 text-white shadow-black/10"
            : "bg-white/95 text-ayurmax-dark shadow-sm"
            } ${isScrolled ? "py-2 px-5" : "py-3 px-7"}`}
        >
          <div className="flex items-center justify-between h-12 md:h-14">

            {/* LOGO */}
            <Link to="/" className="flex items-center">
              <motion.img
                whileHover={{ scale: 1.05 }}
                src="/Ayumaxfinal.png"
                alt="Ayumax"
                className="h-8 md:h-10"
              />
            </Link>

            {/* DESKTOP NAV */}
            <div className="hidden lg:flex items-center gap-1">

              {canAccessDoctorFeatures && (
                <>
                  <NavLink to="/patientlist" isActive={isActive("/patientlist")} icon={<Users size={16} />} theme={theme} isDarkMode={isDarkMode}>
                    Patient List
                  </NavLink>

                  <NavLink to="/addpatient" isActive={isActive("/addpatient")} icon={<UserPlus size={16} />} theme={theme} isDarkMode={isDarkMode}>
                    Add Patient
                  </NavLink>

                  <NavLink to="/getmandaliinfo" isActive={isActive("/getmandaliinfo")} icon={<User size={16} />} theme={theme} isDarkMode={isDarkMode}>
                    Personal Info
                  </NavLink>

                  <NavLink to="/reports/payments" isActive={isActive("/reports/payments")} icon={<FileText size={16} />} theme={theme} isDarkMode={isDarkMode}>
                    Reports
                  </NavLink>
                </>
              )}

              {/* Calendar → Everyone */}
              <NavLink to="/calendar" isActive={isActive("/calendar")} icon={<Calendar size={16} />} theme={theme} isDarkMode={isDarkMode}>
                Calendar
              </NavLink>

              {/* Admin Only */}
              {isAdmin && (
                <NavLink to="/doctorslist" isActive={isActive("/doctorslist")} icon={<Users size={16} />} theme={theme} isDarkMode={isDarkMode}>
                  Doctors List
                </NavLink>
              )}

            </div>

            {/* RIGHT SECTION */}
            <div className="flex items-center gap-2 md:gap-3">

              {clinicList.length > 0 && (
                <div className="hidden md:flex items-center gap-2">
                  <Building2 size={14} className={isDarkMode ? "text-fresh-300" : "text-ayurmax-primary"} />
                  <select
                    value={doctorInfo.CLINIC_ID || ""}
                    onChange={handleClinicChange}
                    className={`bg-transparent text-xs font-semibold outline-none ${isDarkMode ? "text-white" : "text-ayurmax-dark"}`}
                  >
                    {clinicList.map((c) => (
                      <option key={c.CLINIC_ID} value={c.CLINIC_ID} className="text-black">
                        {c.CLINIC_NAME}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <button
                onClick={toggleTransliteration}
                className={`p-2 rounded-full font-bold w-9 h-9 transition-colors ${isDarkMode
                  ? "bg-white/10 text-fresh-300 hover:bg-white/20"
                  : "bg-black/5 text-ayurmax-primary hover:bg-black/10"
                  }`}
              >
                {isTransliterationEnabled ? 'म' : 'E'}
              </button>

              <button
                onClick={toggleTheme}
                className={`p-2 rounded-full transition-colors ${isDarkMode
                  ? "bg-white/10 text-fresh-300 hover:bg-white/20"
                  : "bg-black/5 text-ayurmax-primary hover:bg-black/10"
                  }`}
              >
                {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
              </button>

              <button
                onClick={logout}
                className="hidden sm:flex px-5 py-2 rounded-full text-xs font-bold text-white items-center gap-2 bg-gradient-to-r from-ayurmax-primary to-ayurmax-dark hover:shadow-lg transition-all"
              >
                Logout <LogOut size={16} />
              </button>

              <button
                className="lg:hidden p-2"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                style={{ color: theme.text }}
              >
                {isMobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
              </button>

            </div>
          </div>

          {/* MOBILE MENU */}
          <AnimatePresence>
            {isMobileMenuOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="lg:hidden mt-4 flex flex-col gap-2"
              >

                {canAccessDoctorFeatures && (
                  <>
                    <MobileNavLink to="/patientlist" icon={<Users size={18} />} label="Patient List" isActive={isActive("/patientlist")} theme={theme} />
                    <MobileNavLink to="/addpatient" icon={<UserPlus size={18} />} label="Add Patient" isActive={isActive("/addpatient")} theme={theme} />
                    <MobileNavLink to="/getmandaliinfo" icon={<User size={18} />} label="Mandali Info" isActive={isActive("/getmandaliinfo")} theme={theme} />
                    <MobileNavLink to="/reports/payments" icon={<FileText size={18} />} label="Reports" isActive={isActive("/reports/payments")} theme={theme} />
                  </>
                )}

                <MobileNavLink to="/calendar" icon={<Calendar size={18} />} label="Calendar" isActive={isActive("/calendar")} theme={theme} />

                {isAdmin && (
                  <MobileNavLink to="/doctorslist" icon={<Users size={18} />} label="Doctors List" isActive={isActive("/doctorslist")} theme={theme} />
                )}

              </motion.div>
            )}
          </AnimatePresence>

        </motion.nav>
      </div>

      <div className="h-24 md:h-28" />
    </>
  );
};

/* DESKTOP LINK */
const NavLink = ({ to, isActive, icon, children, isDarkMode }) => (
  <Link to={to}>
    <motion.div
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      className={`relative px-4 py-2 rounded-full text-xs font-semibold flex items-center gap-2 overflow-hidden transition-colors ${isActive
        ? (isDarkMode ? "text-fresh-300 bg-white/10" : "text-ayurmax-primary bg-ayurmax-primary/10")
        : (isDarkMode ? "text-gray-300 hover:text-white" : "text-gray-600 hover:text-ayurmax-dark")
        }`}
    >
      {icon} {children}
    </motion.div>
  </Link>
);

/* MOBILE LINK */
const MobileNavLink = ({ to, icon, label, isActive, theme }) => (
  <Link to={to}>
    <div
      className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${isActive
        ? "bg-ayurmax-primary/10 text-ayurmax-primary dark:text-fresh-300 dark:bg-white/10"
        : "text-gray-700 dark:text-gray-300"
        }`}
    >
      {icon}
      <span className="font-semibold text-sm">{label}</span>
    </div>
  </Link>
);

export default CNavbar;
